import React from 'react';
import { Typography, IconButton, Card, CardContent, Stack, Chip, Box } from '@mui/material';
import { FancySkeleton } from '../common/FancySkeleton';
import {
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { Simulation } from '../../hooks/useSimulations';

interface SimulationCardProps {
  simulation: Simulation; 
  onView: (id: string) => void;
  getReputationImpact: (sim: Simulation) => any;
  getConfidenceLevel: (sim: Simulation) => any;
}

const SimulationCard: React.FC<SimulationCardProps> = ({ 
  simulation, 
  onView, 
  getReputationImpact, 
  getConfidenceLevel 
}) => {
  const hasForecast = !!simulation.resultJson?.data?.forecast;
  const reputationInfo = hasForecast ? getReputationImpact(simulation) : null;
  const confidenceInfo = hasForecast ? getConfidenceLevel(simulation) : null;
  const eventTypeRaw = simulation.resultJson?.data?.forecast?.eventType || 'unknown';
  const eventType = eventTypeRaw.charAt(0).toUpperCase() + eventTypeRaw.slice(1);
  
  // Get creator name
  let creatorName = 'Unknown';
  if (simulation.user) {
    if (simulation.user.firstName && simulation.user.lastName) {
      creatorName = `${simulation.user.firstName} ${simulation.user.lastName}`;
    } else if (simulation.user.firstName) {
      creatorName = simulation.user.firstName;
    } else if (simulation.user.lastName) {
      creatorName = simulation.user.lastName;
    } else if (simulation.user.email) {
      creatorName = simulation.user.email;
    }
  }

  return (
    <Card 
      sx={{ 
        mb: 2, 
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 2
        }
      }}
      onClick={() => onView(simulation.id)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
            {eventType}
          </Typography>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onView(simulation.id);
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Box>
        
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Platform:</Typography>
            <Typography variant="body2">{simulation.platform || 'Unknown'}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Account:</Typography>
            <Typography variant="body2">{simulation.sender || 'Unknown'}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Sector:</Typography>
            <Typography variant="body2">{simulation.sector || 'Unknown'}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Created by:</Typography>
            <Typography variant="body2">{creatorName}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Created:</Typography>
            <Typography variant="body2">{new Date(simulation.createdAt).toLocaleDateString()}</Typography>
          </Box>
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Reputation Impact:</Typography>
            {hasForecast ? (
              <Chip 
                label={reputationInfo!.level}
                size="small"
                sx={{ 
                  backgroundColor: reputationInfo!.color + '20',
                  color: reputationInfo!.color,
                  fontWeight: 'medium'
                }}
              />
            ) : (
              <FancySkeleton height={18} width={120} radius={10} />
            )}
          </Box>
          
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Confidence:</Typography>
            {hasForecast ? (
              <Chip 
                label={confidenceInfo!.level}
                size="small"
                sx={{ 
                  backgroundColor: confidenceInfo!.color + '20',
                  color: confidenceInfo!.color,
                  fontWeight: 'medium'
                }}
              />
            ) : (
              <FancySkeleton height={18} width={80} radius={10} />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {simulation.hasImage && (
            <Chip 
              icon={<ImageIcon />} 
              label="Has Image" 
              size="small" 
              variant="outlined"
            />
          )}
          {simulation.isPrivate && (
            <Chip 
              icon={<LockIcon />} 
              label="Private" 
              size="small" 
              variant="outlined"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SimulationCard;
